// League Wire: unified pickups/drops/executed-trades ledger (replaces Recent Activity).
import React from 'react';
import { LeagueTransaction, Trade } from '../../types';
import { buildWireEntries, WireEntry } from '../../utils/leagueWire';
import { timeAgo } from '../../utils/timeAgo';

interface Props {
  transactions: LeagueTransaction[];
  trades: Trade[];
  loading: boolean;
}

const STAMP_STYLES: Record<WireEntry['kind'], string> = {
  pickup: 'bg-secondary-900 text-gold',
  drop: 'bg-secondary-50 text-ink-900 border border-secondary-900',
  trade: 'bg-success-700 text-white',
};

function posBadge(position: string) {
  return position === 'K' ? 'bg-secondary-900 text-gold' : 'bg-success-100 text-success-800';
}

function PlayerList({ players }: { players: { name: string; position: string }[] }) {
  return (
    <>
      {players.map((p, i) => (
        <span key={`${p.name}-${i}`}>
          {i > 0 && ', '}
          <span className="font-medium">{p.name}</span>
          <span className={`ml-1 text-xs px-1 py-0.5 font-medium ${posBadge(p.position)}`}>{p.position}</span>
        </span>
      ))}
    </>
  );
}

function WireRow({ entry }: { entry: WireEntry }) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 px-5 py-3">
      {/* toUpperCase() matters: the CSS `uppercase` class doesn't change textContent,
          and the tests assert getByText('PICKUP') etc. */}
      <span className={`font-display uppercase text-[10px] tracking-wider px-1.5 py-0.5 shrink-0 self-start ${STAMP_STYLES[entry.kind]}`}>
        {entry.kind.toUpperCase()}
      </span>
      <div className="flex-1 min-w-0 text-sm text-secondary-900">
        {entry.kind === 'trade' ? (
          <>
            <span className="font-medium">{entry.teamAName}</span>
            {' ⇄ '}
            <span className="font-medium">{entry.teamBName}</span>
            {entry.aReceives.length > 0 && (
              <span className="block sm:inline sm:ml-1.5 text-secondary-700">
                {entry.teamAName} received <PlayerList players={entry.aReceives} />
              </span>
            )}
            {entry.bReceives.length > 0 && (
              <span className="block sm:inline sm:ml-1.5 text-secondary-700">
                {entry.bReceives.length > 0 && entry.aReceives.length > 0 ? '· ' : ''}
                {entry.teamBName} received <PlayerList players={entry.bReceives} />
              </span>
            )}
          </>
        ) : (
          <>
            <span className="font-medium">{entry.teamName}</span>
            {' '}{entry.kind === 'pickup' ? 'signed' : 'released'}{' '}
            <span className="font-medium">{entry.playerName}</span>
            <span className={`ml-1.5 text-xs px-1 py-0.5 font-medium ${posBadge(entry.playerPosition)}`}>
              {entry.playerPosition}
            </span>
            {entry.nflTeam && <span className="ml-1 text-xs text-secondary-400">{entry.nflTeam}</span>}
            {entry.week != null && <span className="ml-1.5 text-xs text-secondary-400">Wk {entry.week}</span>}
          </>
        )}
      </div>
      <span className="text-xs text-secondary-400 shrink-0">{timeAgo(entry.ts)}</span>
    </div>
  );
}

const LeagueWire: React.FC<Props> = ({ transactions, trades, loading }) => {
  const entries = buildWireEntries(transactions, trades);

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="font-display uppercase text-lg tracking-wide text-ink-900">League Wire</h3>
      </div>
      {loading ? (
        <div className="flex items-center justify-center py-6">
          <div className="loading-spinner w-4 h-4 mr-2" />
          <span className="text-secondary-500 text-sm">Loading the wire...</span>
        </div>
      ) : entries.length === 0 ? (
        <div className="card-body py-6 text-center text-secondary-500 text-sm">
          No moves yet — the wire is quiet.
        </div>
      ) : (
        <div className="divide-y divide-secondary-100">
          {entries.map((e, i) => <WireRow key={`${e.kind}-${e.ts}-${i}`} entry={e} />)}
        </div>
      )}
    </div>
  );
};

export default LeagueWire;
